<script setup>

import { inject, ref, computed, watch, onMounted, nextTick } from 'vue';
import ListeAutoCompletion from '@/components/generiques/ListeAutoCompletion.vue';;

const { conteneurFormule } = defineProps({
  conteneurFormule: Object
});

const emit = defineEmits(['enregistrer', 'annuler']);

const getTypeObjet = inject('getTypeObjet');

const texteFormule = ref(conteneurFormule.formule != null ? conteneurFormule.formule.texte.replace(/\n$/, '') : `${conteneurFormule.nom} = `);
const composantSaisie = ref(null);
const autoCompletionEstAffichee = ref(false);
const autoCompletionEstActivee = ref(false);
const evenementAutoCompletion = ref({});

const tabulation = "    ";
const motsClesAvantIdentation = ['if', 'elif', 'else', 'match', 'case', 'for'];

const typeObjet = computed(() => {
	return getTypeObjet(conteneurFormule.idTypeObjet);
});

onMounted(() => {
	composantSaisie.value.focus();
});

watch(() => conteneurFormule.demandeEnregistrement, (demandeEnregistrement) => {
	if (demandeEnregistrement) {
		delete conteneurFormule.demandeEnregistrement;
		enregistrer();
	}
});

function modifierTexteFormule(texte) {
	texteFormule.value = texte;
}

function gererToucheClavier(event) {
	const touche = event.key;
	switch (touche) {
		case 'Escape':
			event.stopPropagation();
			annuler();
			break;
		case 'Enter':
			event.preventDefault();
			event.stopPropagation();
			if (event.shiftKey) {
				enregistrer();
			} else {
				if (autoCompletionEstAffichee.value) {
					evenementAutoCompletion.value.validation = true;
				} else {
					insererRetourLigne();
				}
			}
			break;
		case 'Tab': {
			event.preventDefault();
			event.stopPropagation();
			let selection = getSelectionTexte();
			let positionDebLigne = getPositionDebutLigne(selection.positionDebut);
			if (event.shiftKey) {
				if (getNombreTabulations(positionDebLigne) > 0) {
					supprimerTexte(positionDebLigne, tabulation.length, selection);
				}
				positionDebLigne = getPositionDebutLigneSuivante(positionDebLigne);
				while (positionDebLigne < selection.positionFin) {
					if (getNombreTabulations(positionDebLigne) > 0) {
						supprimerTexte(positionDebLigne, tabulation.length, selection);
					}
					positionDebLigne = getPositionDebutLigneSuivante(positionDebLigne);
				}
				selectionnerTexte(selection);
			} else {
				if (texteFormule.value.substring(positionDebLigne, selection.positionDebut).trim().length > 0) {
					activerAutoCompletion(!autoCompletionEstActivee.value);
				} else {
					insererTexte(tabulation, positionDebLigne, positionDebLigne, selection);
					positionDebLigne = getPositionDebutLigneSuivante(positionDebLigne);
					while (positionDebLigne < selection.positionFin) {
						insererTexte(tabulation, positionDebLigne, positionDebLigne, selection);
						positionDebLigne = getPositionDebutLigneSuivante(positionDebLigne);
					}
					selectionnerTexte(selection);
				}
			}
			break;
		}
		case 'Backspace': {
			let selection = getSelectionTexte();
			let positionDebLigne = getPositionDebutLigne(selection.positionDebut);
			if (selection.positionDebut > positionDebLigne && selection.positionDebut == selection.positionFin && texteFormule.value.substring(positionDebLigne, selection.positionDebut) === tabulation.repeat((selection.positionDebut - positionDebLigne) / tabulation.length)) {
				event.preventDefault();
				event.stopPropagation();
				supprimerTexte(positionDebLigne, tabulation.length, selection);
				selectionnerTexte(selection);
			}
			break;
		}
		case 'z':       // Ne pas annuler dernière commande
			if (event.ctrlKey) {
				event.stopPropagation();
			}
			break;
		case 'y':       // Ne pas refaire dernière commande
			if (event.ctrlKey) {
				event.stopPropagation();
			}
			break;
		case 'ArrowUp':
			selectionnerSuggestionSuivante(event, -1);
			break;
		case 'ArrowDown':
			selectionnerSuggestionSuivante(event, 1);
			break;
		case 'PageUp':
			selectionnerSuggestionSuivante(event, -10);
			break;
		case 'PageDown':
			selectionnerSuggestionSuivante(event, 10);
			break;
		default:
			if (autoCompletionEstActivee.value) {
				autoCompletionEstActivee.value = false;
				setTimeout(() => autoCompletionEstActivee.value = true, 10);
			}

	}
}

function getSelectionTexte() {
	return {
		positionDebut: composantSaisie.value.selectionStart,
		positionFin: composantSaisie.value.selectionEnd
	};
}

function selectionnerTexte(selection) {
	nextTick(() => {
		composantSaisie.value.selectionStart = selection.positionDebut;
		composantSaisie.value.selectionEnd = selection.positionFin;
	});
}

function getNombreTabulations(positionDebutLigne) {
	let nbTabulations = 0;
	let nbCaracteresTabulation = tabulation.length;
	let positionCourante = positionDebutLigne;
	while (positionCourante + nbCaracteresTabulation <= texteFormule.value.length && texteFormule.value.substring(positionCourante, positionCourante + nbCaracteresTabulation) === tabulation) {
		nbTabulations++;
		positionCourante += nbCaracteresTabulation;
	}
	return nbTabulations;
}

function getPremierMot(positionDebutLigne) {
	const regex = new RegExp(`(${tabulation})*([^ :]+).*`);
	const match = texteFormule.value.substring(positionDebutLigne).match(regex);
	if (match != null && match.length > 2) {
		return match[2];
	} else {
		return '';
	}
}

function getPositionDebutLigne(position) {
	let caractere;
	let positionCourante = position;
	while (positionCourante > 0 && caractere !== '\n') {
		positionCourante--;
		caractere = texteFormule.value.charAt(positionCourante);
	}
	return(positionCourante == 0 ? positionCourante : positionCourante + 1);
}

function getPositionDebutLigneSuivante(position) {
	let caractere = texteFormule.value.charAt(position);
	let positionCourante = position;
	const longueurTexte = texteFormule.value.length;
	while (positionCourante < longueurTexte && caractere !== '\n') {
		positionCourante++;
		caractere = texteFormule.value.charAt(positionCourante);
	}
	positionCourante++;
	return(positionCourante);
}

function insererTexte(texteInsere, positionDebut, positionFin, selection) {
	let texteAvant = texteFormule.value.substring(0, positionDebut);
	let texteApres = texteFormule.value.substring(positionFin);
	modifierTexteFormule(`${texteAvant}${texteInsere}${texteApres}`);
	if (selection.positionDebut >= positionFin) {
		selection.positionDebut += positionFin - positionDebut + texteInsere.length;
	} else if (selection.positionDebut >= positionDebut) {
		selection.positionDebut = positionDebut + texteInsere.length;
	}
	if (selection.positionFin > positionFin) {
		selection.positionFin += positionFin - positionDebut + texteInsere.length;
	} else if (selection.positionFin >= positionDebut) {
		selection.positionFin = positionDebut + texteInsere.length;
	}
}

function supprimerTexte(position, longueur, selection) {
	let texteAvant = texteFormule.value.substring(0, position);
	let texteApres = texteFormule.value.substring(position + longueur);
	modifierTexteFormule(`${texteAvant}${texteApres}`);
	if (selection.positionDebut >= position + longueur) {
		selection.positionDebut -= longueur;
	} else if (selection.positionDebut >= position) {
		selection.positionDebut = position;
	}
	if (selection.positionFin > position + longueur) {
		selection.positionFin -= longueur;
	} else if (selection.positionFin >= position) {
		selection.positionFin = position;
	}
}

function insererRetourLigne() {
	let positionDebSelection = composantSaisie.value.selectionStart;
	let positionFinSelection = composantSaisie.value.selectionEnd;
	let selection = getSelectionTexte();
	let positionDebLigne = getPositionDebutLigne(positionDebSelection);
	let nbTabulations = getNombreTabulations(positionDebLigne);
	let premierMotLigneDeb = getPremierMot(positionDebLigne);
	if (motsClesAvantIdentation.includes(premierMotLigneDeb)) {
		nbTabulations++;
	}
	let texteInsere = `\n${tabulation.repeat(nbTabulations)}`;
	insererTexte(texteInsere, positionDebSelection, positionFinSelection, selection);
	selectionnerTexte(selection);
	nextTick(() => autoCompletionEstAffichee.value = false);
}

function activerAutoCompletion(activee) {
	autoCompletionEstActivee.value = activee;
}

function selectionnerSuggestionSuivante(event, increment) {
	if (autoCompletionEstAffichee.value) {
		event.preventDefault();
		evenementAutoCompletion.value.increment = increment;
	}
}

function validerAutoCompletion(infosCompletion) {
	const positionDebMotComplete = infosCompletion.expressionCourante.positionDebutMotCourant;
	const dernierMot = infosCompletion.expressionCourante.dernierMot;
	const motComplete = infosCompletion.motComplete;
	const selection = getSelectionTexte();
	supprimerTexte(positionDebMotComplete, dernierMot.length, selection);
	insererTexte(motComplete, selection.positionDebut, selection.positionDebut, selection);
	selectionnerTexte(selection);
	nextTick(() => autoCompletionEstAffichee.value = false);
}

function enregistrer() {
	emit('enregistrer', texteFormule.value);
}

function annuler() {
	emit('annuler');
}

</script>

<template>
	<div class="panel-formule">
		<textarea class="texte-expression" 
			ref="composantSaisie"
			:rows="10"
			:cols="150"
			:value="texteFormule"
			spellcheck="false"
			@input="modifierTexteFormule($event.target.value)"
			@blur="activerAutoCompletion(false)"
			@keydown="gererToucheClavier($event)" />
		<ListeAutoCompletion class="liste-auto-completion" 
			:activee="autoCompletionEstActivee"
			v-model:affichee="autoCompletionEstAffichee"
			:type-objet="typeObjet"
			:variables="conteneurFormule.formule.variables"
			:texte="composantSaisie?.value"
			:position-curseur="composantSaisie?.selectionStart"
			:evenement="evenementAutoCompletion"
			@validation="validerAutoCompletion"
			@touche-entree="insererRetourLigne" />
	</div>
</template>

<style scoped>

.panel-formule {
	white-space: pre;
	width: 100%;
	height: 100%;
	border: none !important;
	outline: none !important;
	resize: both;
}

.liste-auto-completion {
	position: fixed;
	top: 55px;
	right: 20px;
}

</style>